import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';
import { AddModelProviderDialog } from '../components/modelcatalog/AddModelProviderDialog';
import { ModelCatalogReviewTab } from '../components/modelcatalog/review/ModelCatalogReviewTab';
import { ModelsSummaryStrip } from '../components/models/ModelsSummaryStrip';
import { ProviderRail } from '../components/models/ProviderRail';
import { ProviderCard } from '../components/models/ProviderCard';
import { ProviderModelsTable } from '../components/models/ProviderModelsTable';
import {
  configCountsByModel,
  differenceCounts,
  differsByModel,
  modelsByProvider as groupModelsByProvider,
} from '../components/models/modelsPageData';
import { useFlag } from '../hooks/useFeatureFlags';
import { useModelCatalogReview } from '../hooks/useModelCatalogReview';
import { useModelConfigs } from '../hooks/useModelConfigs';
import { useModelProviders, useModels } from '../hooks/useModels';
import type { Model, ModelProvider } from '../types';

/**
 * Models — Advanced › Models (task 1167). One page in place of Model Catalog
 * and Model Configs.
 *
 * Report: `project/reports/innovate-app-model-catalog-unified-design.md`.
 * Design: `project/artifacts/innovate-models-unified-mockup.html`, Frame 1.
 *
 * Two views. **Browse** (`/model-catalog`): the summary strip, Model Providers
 * as the left rail, the selected provider's card (key cell, account keys,
 * Models), and its Models table with each Model's configs inline. **Review**
 * (`/model-catalog/review`, behind `innovate.fe.models.review`): the existing
 * provider comparison, unchanged, optionally narrowed to one provider or Model;
 * its badge carries the open difference count and "Check against providers"
 * runs it from the header.
 *
 * Query parameters, on both views: `provider` (a provider KEY) and `model` (a
 * Model row id). Browse selects that provider (or the Model's) and opens the
 * Model expanded; `addConfig=1` also opens Add config on it — the target of
 * the retired Model Config pages' redirects. Review narrows to them.
 *
 * Writes keep their flags: `innovate.fe.model-catalog.manage` for providers
 * and Models, `innovate.fe.model-configs.create` / `.delete` for configs.
 */

const BROWSE_PATH = '/model-catalog';
const REVIEW_PATH = '/model-catalog/review';
/** Every config, for the Configs column and the strip. A catalog has a few dozen. */
const ALL_CONFIGS_LIMIT = 500;

const MESSAGE_CLASSES = 'flex items-center justify-center h-40 text-sm';

function pickProvider(
  providers: ModelProvider[],
  models: Model[],
  providerKey: string | null,
  modelRowId: string | null,
): ModelProvider | null {
  if (modelRowId) {
    const model = models.find((m) => m.id === modelRowId);
    const owner = model && providers.find((p) => p.id === model.providerId);
    if (owner) return owner;
  }
  if (providerKey) {
    const named = providers.find((p) => p.key === providerKey);
    if (named) return named;
  }
  return (
    providers.find((p) => p.isPlatformDefault) ?? providers.find((p) => p.enabled) ?? providers[0] ?? null
  );
}

export default function ModelsPage() {
  const canManage = useFlag('innovate.fe.model-catalog.manage');
  const canReview = useFlag('innovate.fe.models.review');
  const canCreateConfig = useFlag('innovate.fe.model-configs.create');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const onReviewPath = pathname.replace(/\/+$/, '') === REVIEW_PATH;
  const view: 'browse' | 'review' = onReviewPath && canReview ? 'review' : 'browse';
  const providerKey = searchParams.get('provider');
  const modelRowId = searchParams.get('model');
  const addConfig = searchParams.get('addConfig') === '1';

  const providersQuery = useModelProviders();
  // Platform staff see retired Models here too (task 1104); pickers keep hiding them.
  const modelsQuery = useModels({ includeRetired: true });
  // Platform staff see configs on switched-off providers too (task 1226); pickers keep hiding them.
  const configsQuery = useModelConfigs(1, ALL_CONFIGS_LIMIT, { includeDisabledProviders: true });
  const review = useModelCatalogReview({ enabled: canReview });
  const [addingProvider, setAddingProvider] = useState(false);

  const providers = useMemo(() => providersQuery.data ?? [], [providersQuery.data]);
  const models = useMemo(() => modelsQuery.data ?? [], [modelsQuery.data]);
  const configs = useMemo(() => configsQuery.data?.data ?? [], [configsQuery.data]);
  const reviewDoc = canReview ? review.data : undefined;

  const byProvider = useMemo(() => groupModelsByProvider(models), [models]);
  const configCounts = useMemo(() => configCountsByModel(configs), [configs]);
  const differs = useMemo(() => differsByModel(reviewDoc), [reviewDoc]);
  const differenceTotal = differenceCounts(reviewDoc).total;

  const selected = pickProvider(providers, models, providerKey, modelRowId);

  const selectProvider = (id: string) => {
    const provider = providers.find((p) => p.id === id);
    if (!provider) return;
    setSearchParams({ provider: provider.key }, { replace: true });
  };

  const reviewPathFor = canReview
    ? (model: Model) => `${REVIEW_PATH}?model=${encodeURIComponent(model.id)}`
    : null;
  const providerReviewPath =
    canReview && selected ? `${REVIEW_PATH}?provider=${encodeURIComponent(selected.key)}` : null;

  const viewButton = (id: 'browse' | 'review', label: string, badge?: number) => (
    <button
      type="button"
      role="tab"
      aria-selected={view === id}
      data-testid={`models-view-${id}`}
      onClick={() => {
        if (id === view) return;
        // The selection rides along, so Browse ↔ Review keeps its place.
        const keep = new URLSearchParams();
        if (providerKey) keep.set('provider', providerKey);
        if (modelRowId) keep.set('model', modelRowId);
        const query = keep.toString();
        navigate(`${id === 'review' ? REVIEW_PATH : BROWSE_PATH}${query ? `?${query}` : ''}`, { replace: true });
      }}
      className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-sm font-medium ${
        view === id ? 'bg-indigo-600 text-white' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
      }`}
    >
      {label}
      {badge !== undefined && badge > 0 && (
        <span
          data-testid="models-review-badge"
          className={`rounded-full px-1.5 text-xs font-semibold ${
            view === id ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
          }`}
        >
          {badge}
        </span>
      )}
    </button>
  );

  const headerActions = (
    <div className="flex flex-wrap items-center gap-3">
      {canReview && (
        <div role="tablist" aria-label="Models views" className="inline-flex rounded-lg border border-gray-300 p-0.5 dark:border-gray-600">
          {viewButton('browse', 'Browse')}
          {viewButton('review', 'Review', reviewDoc ? differenceTotal : undefined)}
        </div>
      )}
      {canReview && (
        <button
          type="button"
          onClick={() => review.refresh()}
          disabled={review.isRefreshing}
          data-testid="models-check-providers"
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <ArrowPathIcon className={`h-4 w-4 ${review.isRefreshing ? 'animate-spin' : ''}`} />
          {review.isRefreshing ? 'Checking…' : 'Check against providers'}
        </button>
      )}
    </div>
  );

  const loading = providersQuery.isLoading || modelsQuery.isLoading;
  const failed = providersQuery.error || modelsQuery.error;

  let body;
  if (view === 'review') {
    const scopeLabel = modelRowId
      ? models.find((m) => m.id === modelRowId)?.modelId ?? 'one Model'
      : providerKey
        ? providers.find((p) => p.key === providerKey)?.displayName ?? providerKey
        : null;
    body = (
      <div className="mt-6 space-y-4">
        {scopeLabel && (
          <div
            data-testid="models-review-scope"
            className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-sm text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200"
          >
            <span>Reviewing {scopeLabel}</span>
            <button type="button" onClick={() => navigate(REVIEW_PATH, { replace: true })} className="text-xs underline">
              Show all
            </button>
          </div>
        )}
        <ModelCatalogReviewTab scope={{ providerKey: modelRowId ? null : providerKey, modelRowId }} />
      </div>
    );
  } else if (loading) {
    body = <div className={`${MESSAGE_CLASSES} text-gray-500 dark:text-gray-400`}>Loading models…</div>;
  } else if (failed) {
    body = <div className={`${MESSAGE_CLASSES} text-red-500 dark:text-red-400`}>Error loading models. Please try again.</div>;
  } else {
    const selectedModels = selected ? byProvider.get(selected.id) ?? [] : [];
    body = (
      <>
        <ModelsSummaryStrip
          providers={providers}
          models={models}
          configs={configs}
          review={reviewDoc}
          reviewPath={canReview ? REVIEW_PATH : null}
        />
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <ProviderRail
            providers={providers}
            modelsByProvider={byProvider}
            selectedId={selected?.id ?? null}
            onSelect={selectProvider}
            canManage={canManage}
            onAddProvider={() => setAddingProvider(true)}
          />
          <div className="min-w-0">
            {selected ? (
              <>
                <ProviderCard provider={selected} models={selectedModels} canManage={canManage} reviewPath={providerReviewPath} />
                <ProviderModelsTable
                  key={selected.id}
                  provider={selected}
                  providers={providers}
                  models={selectedModels}
                  catalog={models}
                  configCounts={configCounts}
                  differs={differs}
                  canManage={canManage}
                  canCreateConfig={canCreateConfig}
                  reviewPathFor={reviewPathFor}
                  expandModelId={modelRowId}
                  addConfigOnOpen={addConfig}
                />
              </>
            ) : (
              <div className={`${MESSAGE_CLASSES} text-gray-500 dark:text-gray-400`}>No model providers.</div>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <div>
      <PageHeader
        title="Models"
        description="Model Providers, their Models, and the Model Configs agents run on. Curated by Monarch Works platform staff."
        actions={headerActions}
      />
      {body}
      {canManage && <AddModelProviderDialog open={addingProvider} onClose={() => setAddingProvider(false)} />}
    </div>
  );
}
